import { useState } from "react";
import allData from './checklist_config.json';

const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "hi", label: "हिन्दी" },
  { code: "mr", label: "मराठी" },
  { code: "te", label: "తెలుగు" },
  { code: "ta", label: "தமிழ்" },
  { code: "ml", label: "മലയാളം" },
  { code: "kn", label: "ಕನ್ನಡ" },
  { code: "pa", label: "ਪੰਜਾਬੀ" },
  { code: "bn", label: "বাংলা" }
];

const phaseColors = {
  1: { color: "#F97316", bg: "#FFF7ED", border: "#FDBA74" },
  2: { color: "#22C55E", bg: "#F0FDF4", border: "#86EFAC" },
  3: { color: "#3B82F6", bg: "#EFF6FF", border: "#93C5FD" }
};

function buildAnswers(rawAnswers) {
  const a = {};
  a.spouse_alive = rawAnswers.spouse_alive === true;
  a.was_govt = rawAnswers.employment === 0;
  a.has_nominee = rawAnswers.nominee === 0;
  const assets = rawAnswers.assets || [];
  ["has_bank","has_fd","has_lic","has_land","has_house","has_vehicle","has_post_office"].forEach(k => {
    a[k] = assets.includes(k);
  });
  return a;
}

function shouldShow(task, a) {
  if (task.always_show) return true;
  if (!task.show_if) return false;
  return Object.entries(task.show_if).every(([k, v]) => a[k] === v);
}

export default function AntimKaam() {
  const [lang, setLang] = useState("en");
  const [step, setStep] = useState(0);
  const [rawAnswers, setRawAnswers] = useState({});
  const [multiSel, setMultiSel] = useState([]);
  const [doneItems, setDoneItems] = useState({});
  const [expandedItems, setExpandedItems] = useState({});

  const data = allData[lang];
  const questions = data.questions;
  const tasks = data.tasks;
  const cl = data.checklist;
  const intro = data.intro;

  const isChecklist = step > questions.length;
  const currentQ = questions[step - 1];

  const answers = buildAnswers(rawAnswers);
  const filteredTasks = isChecklist ? tasks.filter(t => shouldShow(t, answers)) : [];
  const totalDone = Object.values(doneItems).filter(Boolean).length;
  const pct = filteredTasks.length ? Math.round((totalDone / filteredTasks.length) * 100) : 0;

  function handleSingleAnswer(qid, idx) {
    setRawAnswers(a => ({ ...a, [qid]: idx }));
    setStep(s => s + 1);
  }

  function handleBool(val) {
    setRawAnswers(a => ({ ...a, [currentQ.id]: val }));
    setStep(s => s + 1);
  }

  function handleMultiContinue() {
    setRawAnswers(a => ({ ...a, assets: [...multiSel] }));
    setMultiSel([]);
    setStep(s => s + 1);
  }

  function toggleMulti(key) {
    setMultiSel(s => s.includes(key) ? s.filter(k => k !== key) : [...s, key]);
  }

  function toggleDone(id) {
    setDoneItems(d => ({ ...d, [id]: !d[id] }));
  }

  function toggleExpand(id) {
    setExpandedItems(e => ({ ...e, [id]: !e[id] }));
  }

  function restart() {
    setStep(0);
    setRawAnswers({});
    setDoneItems({});
    setExpandedItems({});
    setMultiSel([]);
  }

  const continueLabel = { bn: "পরবর্তী →", hi: "आगे बढ़ें →", mr: "पुढे जा →", te: "కొనసాగించండి →", ta: "தொடர்க →", ml: "തുടരൂ →", kn: "ಮುಂದುವರಿಸಿ →", pa: "ਅੱਗੇ ਜਾਓ →", en: "Continue →" }[lang];
  const backLabel = { bn: "← পিছনে", hi: "← वापस", mr: "← मागे", te: "← వెనక్కి", ta: "← பின்", ml: "← തിരിച്ച്", kn: "← ಹಿಂದೆ", pa: "← ਵਾਪਸ", en: "← Back" }[lang];

  const wrap = { minHeight: "100vh", background: "#FAFAF9", display: "flex", justifyContent: "center", alignItems: "flex-start", padding: "32px 16px 60px", fontFamily: "Georgia, serif" };
  const card = { width: "100%", maxWidth: 600, background: "white", borderRadius: 20, boxShadow: "0 4px 32px rgba(0,0,0,0.07)", overflow: "hidden" };
  const hdr = { background: "#1C1917", padding: "28px 28px 24px" };

  // INTRO
  if (step === 0) return (
    <div style={wrap}>
      <div style={card}>
        <div style={hdr}>
          <div style={{ fontSize: 24, marginBottom: 8 }}>🕯️</div>
          <h1 style={{ fontFamily: "Georgia, serif", fontSize: 24, fontWeight: 600, color: "white", margin: "0 0 6px" }}>{intro.title}</h1>
          <p style={{ fontFamily: "system-ui,sans-serif", fontSize: 13, color: "#A8A29E", margin: 0, lineHeight: 1.6 }}>{intro.subtitle}</p>
          <a href="https://www.vijaykumarbhoyar.com" target="_blank" rel="noopener noreferrer" style={{ fontFamily: "system-ui,sans-serif", fontSize: 12, color: "#6B7280", marginTop: 16, paddingTop: 16, borderTop: "1px solid #292524", marginBottom: 0, display: "block", textDecoration: "none" }}>{intro.createdBy} ↗</a>
        </div>
        <div style={{ padding: "24px 28px 32px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 20 }}>
            {LANGUAGES.map(l => (
              <button key={l.code} onClick={() => setLang(l.code)} style={{ padding: "8px 4px", borderRadius: 10, border: `1.5px solid ${lang === l.code ? "#1C1917" : "#E5E7EB"}`, background: lang === l.code ? "#1C1917" : "white", color: lang === l.code ? "white" : "#6B7280", fontFamily: "system-ui,sans-serif", fontSize: 12, fontWeight: 500, cursor: "pointer", textAlign: "center" }}>{l.label}</button>
            ))}
          </div>
          <p style={{ fontFamily: "system-ui,sans-serif", fontSize: 14.5, color: "#44403C", lineHeight: 1.8, margin: "0 0 10px" }}>{intro.description1}</p>
          <p style={{ fontFamily: "system-ui,sans-serif", fontSize: 14.5, color: "#44403C", lineHeight: 1.8, margin: "0 0 10px" }}>{intro.description2}</p>
          <div style={{ display: "flex", gap: 10, margin: "20px 0" }}>
            {[[intro.meta[0],intro.meta[1]],[intro.meta[2],intro.meta[3]],[intro.meta[4],intro.meta[5]]].map(([b,s],i) => (
              <div key={i} style={{ flex: 1, background: "#FFF7ED", border: "1px solid #FED7AA", borderRadius: 10, padding: "12px" }}>
                <p style={{ fontFamily: "Georgia,serif", fontSize: 13, fontWeight: 600, color: "#C2410C", margin: "0 0 2px" }}>{b}</p>
                <p style={{ fontFamily: "system-ui,sans-serif", fontSize: 11.5, color: "#78716C", margin: 0 }}>{s}</p>
              </div>
            ))}
          </div>
          <button onClick={() => setStep(1)} style={{ width: "100%", padding: "14px", background: "#1C1917", color: "white", border: "none", borderRadius: 12, fontFamily: "system-ui,sans-serif", fontSize: 15, fontWeight: 600, cursor: "pointer" }}>{intro.begin}</button>
        </div>
      </div>
    </div>
  );

  // QUESTIONS
  if (!isChecklist && currentQ) {
    const progPct = ((step - 1) / questions.length) * 100;
    return (
      <div style={wrap}>
        <div style={card}>
          <div style={{ padding: "24px 28px 28px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <span style={{ fontFamily: "system-ui,sans-serif", fontSize: 12, color: "#9CA3AF" }}>{step} / {questions.length}</span>
              {step > 1 && <button onClick={() => setStep(s => s - 1)} style={{ background: "none", border: "none", fontFamily: "system-ui,sans-serif", fontSize: 12, color: "#9CA3AF", cursor: "pointer", padding: 0 }}>{backLabel}</button>}
            </div>
            <div style={{ height: 4, background: "#F3F4F6", borderRadius: 99, marginBottom: 24 }}>
              <div style={{ height: "100%", width: `${progPct}%`, background: "#1C1917", borderRadius: 99, transition: "width 0.3s" }} />
            </div>
            <h2 style={{ fontFamily: "Georgia,serif", fontSize: 19, fontWeight: 600, color: "#1C1917", margin: "0 0 6px", lineHeight: 1.4 }}>{currentQ.text}</h2>
            {currentQ.sub && <p style={{ fontFamily: "system-ui,sans-serif", fontSize: 13, color: "#78716C", margin: "0 0 20px", lineHeight: 1.6 }}>{currentQ.sub}</p>}

            {currentQ.type === "bool" && (
              <div style={{ display: "flex", gap: 10 }}>
                <button style={{ flex: 1, padding: "13px", border: "1.5px solid #E5E7EB", borderRadius: 10, background: "white", color: "#1C1917", fontFamily: "system-ui,sans-serif", fontSize: 14, cursor: "pointer" }} onClick={() => handleBool(true)}>{currentQ.yes}</button>
                <button style={{ flex: 1, padding: "13px", border: "1.5px solid #E5E7EB", borderRadius: 10, background: "white", color: "#1C1917", fontFamily: "system-ui,sans-serif", fontSize: 14, cursor: "pointer" }} onClick={() => handleBool(false)}>{currentQ.no}</button>
              </div>
            )}

            {currentQ.type === "single" && currentQ.options.map((opt, idx) => (
              <button key={idx} onClick={() => handleSingleAnswer(currentQ.id, idx)} style={{ width: "100%", padding: "13px 16px", border: "1.5px solid #E5E7EB", borderRadius: 10, background: "white", color: "#1C1917", fontFamily: "system-ui,sans-serif", fontSize: 14, cursor: "pointer", textAlign: "left", marginBottom: 8, display: "block" }}>{opt}</button>
            ))}

            {currentQ.type === "multi" && (
              <>
                {currentQ.options.map(opt => {
                  const sel = multiSel.includes(opt.key);
                  return (
                    <button key={opt.key} onClick={() => toggleMulti(opt.key)} style={{ width: "100%", padding: "13px 16px", border: `1.5px solid ${sel ? "#1C1917" : "#E5E7EB"}`, borderRadius: 10, background: sel ? "#1C1917" : "white", color: sel ? "white" : "#1C1917", fontFamily: "system-ui,sans-serif", fontSize: 14, cursor: "pointer", textAlign: "left", marginBottom: 8, display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ width: 18, height: 18, borderRadius: 4, border: `1.5px solid ${sel ? "white" : "#D1D5DB"}`, background: sel ? "white" : "transparent", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {sel && <span style={{ fontSize: 11, color: "#1C1917", fontWeight: 700 }}>✓</span>}
                      </span>
                      {opt.label}
                    </button>
                  );
                })}
                <button disabled={multiSel.length === 0} onClick={handleMultiContinue} style={{ width: "100%", padding: "13px", background: multiSel.length === 0 ? "#E5E7EB" : "#1C1917", color: multiSel.length === 0 ? "#9CA3AF" : "white", border: "none", borderRadius: 10, fontFamily: "system-ui,sans-serif", fontSize: 14, fontWeight: 600, cursor: multiSel.length === 0 ? "not-allowed" : "pointer", marginTop: 14 }}>{continueLabel}</button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // CHECKLIST
  return (
    <div style={{ ...wrap, alignItems: "flex-start" }}>
      <div style={{ ...card, maxWidth: 660 }}>
        <div style={hdr}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <h1 style={{ fontFamily: "Georgia,serif", fontSize: 21, fontWeight: 600, color: "white", margin: "0 0 4px" }}>{cl.title}</h1>
              <p style={{ fontFamily: "system-ui,sans-serif", fontSize: 13, color: "#A8A29E", margin: 0 }}>{filteredTasks.length} {cl.subtitle}</p>
            </div>
            <button onClick={restart} style={{ background: "none", border: "1px solid #44403C", borderRadius: 8, padding: "5px 12px", fontFamily: "system-ui,sans-serif", fontSize: 11, color: "#A8A29E", cursor: "pointer" }}>{cl.startOver}</button>
          </div>
        </div>
        <div style={{ padding: "24px 28px 32px" }}>
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontFamily: "system-ui,sans-serif", fontSize: 13, color: "#6B7280", marginBottom: 6 }}>
              <span>{totalDone} {cl.of} {filteredTasks.length} {cl.done}</span>
              <span>{pct}%</span>
            </div>
            <div style={{ height: 6, background: "#F3F4F6", borderRadius: 99 }}>
              <div style={{ height: "100%", width: `${pct}%`, borderRadius: 99, background: "linear-gradient(90deg, #F97316, #22C55E)", transition: "width 0.4s" }} />
            </div>
          </div>

          {[1,2,3].map(phase => {
            const pts = filteredTasks.filter(t => t.phase === phase);
            if (!pts.length) return null;
            const cfg = phaseColors[phase];
            const phaseInfo = cl.phases[phase];
            return (
              <div key={phase} style={{ marginBottom: 24 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                  <div style={{ width: 9, height: 9, borderRadius: "50%", background: cfg.color, flexShrink: 0 }} />
                  <span style={{ fontFamily: "Georgia,serif", fontSize: 15, fontWeight: 600, color: "#1C1917" }}>{phaseInfo.title}</span>
                </div>
                <p style={{ fontFamily: "system-ui,sans-serif", fontSize: 12.5, color: "#78716C", marginBottom: 12, paddingLeft: 17, lineHeight: 1.5 }}>{phaseInfo.sub}</p>
                {pts.map(task => {
                  const done = !!doneItems[task.id];
                  const isExpanded = !!expandedItems[task.id];
                  return (
                    <div key={task.id} style={{ borderRadius: 10, border: `1.5px solid ${done ? "#E5E7EB" : cfg.border}`, background: done ? "#F9FAFB" : cfg.bg, marginBottom: 8, overflow: "hidden", opacity: done ? 0.6 : 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", cursor: "pointer" }} onClick={() => toggleExpand(task.id)}>
                        <div onClick={e => { e.stopPropagation(); toggleDone(task.id); }} style={{ width: 20, height: 20, borderRadius: 5, border: `1.5px solid ${done ? "#9CA3AF" : cfg.color}`, background: done ? "#9CA3AF" : "white", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                          {done && <span style={{ fontSize: 11, color: "white", fontWeight: 700 }}>✓</span>}
                        </div>
                        <span style={{ fontFamily: "system-ui,sans-serif", fontSize: 14, fontWeight: 500, color: done ? "#9CA3AF" : "#1C1917", flex: 1, lineHeight: 1.4, textDecoration: done ? "line-through" : "none" }}>{task.title}</span>
                        <span style={{ fontSize: 11, fontWeight: 500, border: `1px solid ${done ? "#E5E7EB" : cfg.color}`, borderRadius: 20, padding: "2px 8px", flexShrink: 0, fontFamily: "system-ui,sans-serif", color: done ? "#9CA3AF" : cfg.color }}>{task.timeline}</span>
                        <span style={{ color: "#9CA3AF", fontSize: 13, flexShrink: 0 }}>{isExpanded ? "▲" : "▼"}</span>
                      </div>
                      {isExpanded && (
                        <div style={{ padding: "0 14px 14px 44px", borderTop: `1px solid ${cfg.border}30` }}>
                          <p style={{ fontFamily: "system-ui,sans-serif", fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.8, color: "#6B7280", margin: "12px 0 4px" }}>{cl.whereToGo}</p>
                          <p style={{ fontFamily: "system-ui,sans-serif", fontSize: 13, color: "#374151", lineHeight: 1.5, margin: 0 }}>{task.office}</p>
                          <p style={{ fontFamily: "system-ui,sans-serif", fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.8, color: "#6B7280", margin: "12px 0 6px" }}>{cl.documents}</p>
                          <ul style={{ margin: 0, paddingLeft: 16 }}>{task.docs.map((d,i) => <li key={i} style={{ fontFamily: "system-ui,sans-serif", fontSize: 13, color: "#374151", marginBottom: 3 }}>{d}</li>)}</ul>
                          <div style={{ background: cfg.bg, border: `1px solid ${cfg.border}`, borderRadius: 8, padding: "10px 12px", marginTop: 10 }}>
                            <p style={{ fontFamily: "system-ui,sans-serif", fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.8, color: cfg.color, margin: "0 0 4px" }}>{cl.note}</p>
                            <p style={{ fontFamily: "system-ui,sans-serif", fontSize: 13, color: "#374151", margin: 0, lineHeight: 1.6 }}>{task.tip}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}

          <div style={{ background: "#FFF7ED", border: "1px solid #FED7AA", borderRadius: 10, padding: "14px 16px", marginTop: 4 }}>
            <p style={{ fontFamily: "Georgia,serif", fontSize: 14, fontWeight: 600, color: "#C2410C", margin: "0 0 6px" }}>{cl.reminder.title}</p>
            <p style={{ fontFamily: "system-ui,sans-serif", fontSize: 13, color: "#44403C", margin: 0, lineHeight: 1.7 }}>{cl.reminder.text}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
